import React from 'react';
import { motion } from 'framer-motion';
import { SectionWrapper } from '../hoc';
import { technologies } from '../constants';
import { styles } from '../styles';
import { textVariant, fadeIn } from '../utils/motion';

const systemDomains = [
	{
		domain: 'Event-Driven Platforms',
		description: 'Async pipelines processing millions of events per day, replacing legacy triggers with decoupled, independently scalable services.',
		tools: ['Kafka', 'RabbitMQ', 'Redis', 'AWS SQS'],
	},
	{
		domain: 'AI-Assisted Operations',
		description: 'LLM-powered tooling for inspections, code review, and natural-language database queries with guardrails and escalation paths.',
		tools: ['AWS Bedrock', 'LangGraph', 'LangChain', 'FastAPI'],
	},
	{
		domain: 'Financial Workflow Systems',
		description: 'Transaction processing pipelines handling $20B+ annually, re-architected from 9-minute sync flows to 70-second async paths.',
		tools: ['PostgreSQL', 'Redis', 'Python', 'PHP'],
	},
	{
		domain: 'Cloud-Native Infrastructure',
		description: 'Infrastructure-as-code deployments with automated CI/CD, containerized services, and multi-environment orchestration.',
		tools: ['AWS', 'Docker', 'Terraform', 'CI/CD'],
	},
];

const categoryColors = {
	'Languages & Frameworks': 'bg-accent',
	'Cloud & Infrastructure': 'bg-[#6B7B5E]',
	'Data & Messaging': 'bg-[#8B7355]',
	'AI & Agent Systems': 'bg-[#5E6B4E]',
};

const getTechAbbreviation = (name) => {
	const words = name.split(/[\s&.-]+/).filter(Boolean);
	if (words.length >= 2) return `${words[0][0]}${words[1][0]}`.toUpperCase();
	return name.slice(0, 2).toUpperCase();
};

const Tech = () => {
	const categories = Object.entries(technologies);

	return (
		<>
			<motion.div variants={textVariant()}>
				<p className={styles.sectionSubText}>What I Build</p>
				<h2 className={styles.sectionHeadText}>Systems & Stack</h2>
			</motion.div>

			<motion.p
				variants={fadeIn('', '', 0.1, 0.4)}
				className="mt-4 text-muted text-[16px] w-full leading-[28px]">
				I do not just use tools. I choose them for specific system constraints.
				Here is what I build and what I build it with.
			</motion.p>

			<div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
				{systemDomains.map((system, idx) => (
					<motion.div
						key={system.domain}
						initial={{ opacity: 0, y: 20 }}
						whileInView={{ opacity: 1, y: 0 }}
						viewport={{ once: true }}
						transition={{ duration: 0.4, delay: idx * 0.1 }}
						className="bg-warm border border-border rounded-xl p-6 hover:border-accent/20 transition-colors duration-300">
						<h3 className="text-primary text-[16px] font-bold">{system.domain}</h3>
						<p className="text-muted text-[14px] leading-relaxed mt-2">{system.description}</p>
						<div className="mt-4 flex flex-wrap gap-1.5">
							{system.tools.map((tool) => (
								<span key={tool} className="px-2.5 py-1 bg-accent/5 border border-accent/15 rounded-md text-[12px] text-accent font-semibold">
									{tool}
								</span>
							))}
						</div>
					</motion.div>
				))}
			</div>

			<motion.div
				variants={fadeIn('', '', 0.3, 0.4)}
				className="mt-10">
				<p className="text-primary text-[12px] uppercase tracking-[0.2em] font-semibold mb-4">
					Full Technology Stack
				</p>
				<div className="bg-warm border border-border rounded-2xl overflow-hidden divide-y divide-border/60">
					{categories.map(([category, techs], catIndex) => (
						<motion.div
							key={category}
							variants={fadeIn('up', 'tween', catIndex * 0.07, 0.3)}
							className="px-5 py-4 md:px-6 md:py-5">
							<div className="flex flex-col md:flex-row md:items-start gap-3 md:gap-6">
								<div className="md:w-56 flex items-center gap-2.5">
									<div className={`w-2 h-2 rounded-full ${categoryColors[category] || 'bg-accent'}`} />
									<h3 className="text-primary text-[14px] font-semibold">{category}</h3>
								</div>
								<div className="flex-1 flex flex-wrap gap-2">
									{techs.map((tech, techIndex) => (
										<motion.div
											key={tech.name}
											variants={fadeIn('', '', techIndex * 0.02, 0.2)}
											whileHover={{ y: -1 }}
											className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5 bg-surface border border-border/70 transition-all duration-200">
											<div className={`w-6 h-6 rounded-md ${categoryColors[category] || 'bg-accent'} bg-opacity-10 flex items-center justify-center flex-shrink-0`}>
												{tech.icon ? (
													<img src={tech.icon} alt="" className="w-3.5 h-3.5 object-contain" aria-hidden="true" />
												) : (
													<span className="text-[9px] font-bold text-primary/70">
														{getTechAbbreviation(tech.name)}
													</span>
												)}
											</div>
											<span className="text-primary text-[13px] font-medium">{tech.name}</span>
										</motion.div>
									))}
								</div>
							</div>
						</motion.div>
					))}
				</div>
			</motion.div>
		</>
	);
};

export default SectionWrapper(Tech, 'tech');
